import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { PlusIcon, XIcon, TrashIcon, EditIcon, ShieldIcon, UsersIcon, EyeIcon } from '../components/Icons';

const inputCls = "w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-[13.5px] text-slate-700 bg-white outline-none focus:border-[#fb866d] transition-all placeholder:text-slate-400";
const labelCls = "block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5";

const ROLE_META = {
  admin:  { label: 'Full Access', desc: 'Can view, create, edit and delete everything', badge: 'bg-[#0f1b33] text-white', Icon: ShieldIcon },
  viewer: { label: 'Read Only',   desc: 'Can view everything, cannot change anything',  badge: 'bg-slate-100 text-slate-600', Icon: EyeIcon },
};

function initials(name = '') {
  return name.split(' ').slice(0, 2).map(n => n[0] || '').join('').toUpperCase();
}

// ─── Create / Edit modal ─────────────────────────────────────────────────────
function UserModal({ initial, saving, error, onSave, onClose }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    name: initial?.name || '', email: initial?.email || '',
    password: '', role: initial?.role || 'viewer',
  });
  const set = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,27,51,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-[460px] max-w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100 flex-shrink-0">
          <div className="w-10 h-10 rounded-xl bg-[#0f1b33] flex items-center justify-center">
            <UsersIcon size={18} className="text-white" />
          </div>
          <h2 className="text-base font-bold text-slate-800 flex-1">{isEdit ? 'Edit User' : 'New User'}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-all">
            <XIcon size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          <div>
            <label className={labelCls}>Full Name *</label>
            <input className={inputCls} name="name" placeholder="e.g. Mohammed Ali" value={form.name} onChange={set} />
          </div>
          <div>
            <label className={labelCls}>Login Email *</label>
            <input className={inputCls} type="email" name="email" placeholder="user@delta.com" value={form.email} onChange={set} />
          </div>
          <div>
            <label className={labelCls}>{isEdit ? 'New Password (leave blank to keep current)' : 'Password *'}</label>
            <input className={inputCls} name="password" placeholder={isEdit ? '••••••••' : 'Set a password'} value={form.password} onChange={set} />
          </div>
          <div>
            <label className={labelCls}>Access Level</label>
            <div className="space-y-2">
              {Object.entries(ROLE_META).map(([value, m]) => (
                <label key={value}
                  className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                    form.role === value ? 'border-[#fb866d] bg-[#fff7f5]' : 'border-slate-200 hover:border-slate-300'
                  }`}>
                  <input type="radio" name="role" value={value} checked={form.role === value}
                    onChange={set} className="mt-0.5 accent-[#fb866d]" />
                  <span>
                    <span className="block text-[13.5px] font-semibold text-slate-800 flex items-center gap-1.5">
                      <m.Icon size={13} /> {m.label}
                    </span>
                    <span className="block text-[12px] text-slate-500 mt-0.5">{m.desc}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          {error && <div className="px-4 py-3 rounded-xl text-sm bg-red-50 border border-red-200 text-red-600">{error}</div>}
        </div>

        <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-5 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-slate-400 transition-all">
            Cancel
          </button>
          <button onClick={() => onSave(form)} disabled={saving || !form.name || !form.email || (!isEdit && !form.password)}
            className="px-6 py-2.5 rounded-full text-white text-sm font-semibold bg-[#fb866d] hover:bg-[#e8614a] transition-all disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);        // null | {} (create) | user (edit)
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [removeUser, setRemoveUser] = useState(null);
  const [rowError, setRowError] = useState('');

  const load = useCallback(() => {
    api.get('/users')
      .then(r => setUsers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function handleSave(form) {
    setSaving(true); setModalError('');
    try {
      if (modal?.id) {
        const body = { name: form.name, email: form.email, role: form.role };
        if (form.password) body.password = form.password;
        await api.put(`/users/${modal.id}`, body);
      } else {
        await api.post('/users', form);
      }
      setModal(null);
      load();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  }

  async function handleRoleChange(u, role) {
    setRowError('');
    try {
      await api.put(`/users/${u.id}`, { role });
      // Changing your own role takes effect on next login (token keeps old role until then)
      load();
    } catch (err) {
      setRowError(err.response?.data?.error || 'Failed to change role');
    }
  }

  async function handleDelete() {
    if (!removeUser) return;
    setRowError('');
    try {
      await api.delete(`/users/${removeUser.id}`);
      setRemoveUser(null);
      load();
    } catch (err) {
      setRemoveUser(null);
      setRowError(err.response?.data?.error || 'Failed to delete user');
    }
  }

  const admins = users.filter(u => u.role === 'admin').length;

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div>
          <h1 className="text-lg font-bold text-slate-800">Users & Access</h1>
          <p className="text-[13px] text-slate-500 mt-0.5">
            System logins for the HR console. Client attendance-portal logins are managed from the Clients page.
          </p>
        </div>
        <button onClick={() => { setModalError(''); setModal({}); }}
          className="ml-auto flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-[13px] font-semibold bg-[#fb866d] hover:bg-[#e8614a] transition-all shadow-sm">
          <PlusIcon size={15} /> Add User
        </button>
      </div>

      {rowError && (
        <div className="mb-4 px-4 py-3 rounded-xl text-sm bg-red-50 border border-red-200 text-red-600">{rowError}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="px-5 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">User</th>
                <th className="px-5 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Email</th>
                <th className="px-5 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Access</th>
                <th className="px-5 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-5 py-10 text-center text-[13px] text-slate-400">Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={4} className="px-5 py-10 text-center text-[13px] text-slate-400">No users yet</td></tr>
              ) : users.map(u => {
                const meta = ROLE_META[u.role] || ROLE_META.admin;
                const isMe = u.id === me?.id;
                const lastAdmin = u.role === 'admin' && admins <= 1;
                return (
                  <tr key={u.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#fb866d] flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0">
                          {initials(u.name || u.email)}
                        </div>
                        <span className="text-[13.5px] font-semibold text-slate-800">
                          {u.name}{isMe && <span className="ml-2 text-[10px] font-bold text-[#fb866d] uppercase">You</span>}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-[13px] text-slate-500">{u.email}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${meta.badge}`}>
                          <meta.Icon size={11} /> {meta.label}
                        </span>
                        <select value={u.role}
                          onChange={e => handleRoleChange(u, e.target.value)}
                          disabled={lastAdmin}
                          title={lastAdmin ? 'The last full-access user cannot be downgraded' : 'Change access level'}
                          className="px-2 py-1 border border-slate-200 rounded-lg text-[12px] text-slate-600 bg-white outline-none focus:border-[#fb866d] disabled:bg-slate-50 disabled:text-slate-300 disabled:cursor-not-allowed">
                          <option value="admin">Full Access</option>
                          <option value="viewer">Read Only</option>
                        </select>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => { setModalError(''); setModal(u); }} title="Edit / reset password"
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-[#fb866d] hover:bg-[#fff1ee] transition-all">
                          <EditIcon size={14} />
                        </button>
                        <button onClick={() => setRemoveUser(u)} title={isMe ? 'You cannot delete your own account' : 'Delete user'}
                          disabled={isMe || lastAdmin}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-slate-400 disabled:hover:bg-transparent">
                          <TrashIcon size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-4 text-[12px] text-slate-400">
        Role changes apply on the user's next login. Full Access can view, create, edit and delete everything and manage users.
        Read Only can open every page but cannot change anything — the server blocks all edits from read-only accounts.
      </p>

      {modal !== null && (
        <UserModal initial={modal.id ? modal : null} saving={saving} error={modalError}
          onSave={handleSave} onClose={() => setModal(null)} />
      )}

      <ConfirmDialog
        open={!!removeUser}
        tone="danger"
        title={`Delete ${removeUser?.name || 'user'}?`}
        message="They will no longer be able to log in. This cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onClose={() => setRemoveUser(null)}
      />
    </div>
  );
}